<script lang="ts">
  import { onMount } from 'svelte';
  import { SlideToggle } from '@skeletonlabs/skeleton';
  import { Pencil } from 'phosphor-svelte';
  import FileTable from './FileTable.svelte';
  import PrefixesModal from './PrefixesModal.svelte';
  import { CreditCardType, DirectFeedFolder } from '$lib/models/ccr/types';
  import type { DirectFeedConfig, GetDirectFeedFeedFilesResponse, FilenameWhitelist, FeedFilePending } from '$lib/models/ccr/types';
  import {
    directFeedStore,
    creditCardSectionState,
    initCreditCardState,
    setLocalPrefixes,
    setLocalProcessingEnabled,
    addPendingDelete,
    setStarterDataValidationResult,
    initFromServerConfig,
    syncToServer,
    rollbackFromServer,
  } from '$lib/stores/directFeedStore';
  import { showToast } from '$lib/helpers/toastrHelper';
  import { getFileErrorReasons } from '$lib/utils/ccr/ccrFileErrorUtility';

  function hasValidPrefixes(prefixes: FilenameWhitelist[]): boolean {
    return prefixes.length > 0 && prefixes.some((p) => p?.filenamePrefix?.trim() !== '');
  }

  function filterValidPrefixes(prefixes: FilenameWhitelist[]): FilenameWhitelist[] {
    return prefixes.filter((p) => p?.filenamePrefix?.trim() !== '');
  }

  export let clientId: number;
  export let creditCardType: CreditCardType;
  export let title: string;

  let loading = true;
  let config: DirectFeedConfig | null = null;
  let files: GetDirectFeedFeedFilesResponse | null = null;
  let hasErrors = false;
  let allFiles: any[] = [];
  let showPrefixesModal = false;
  let isSaving = false;

  $: cardState = $creditCardSectionState.get(creditCardType);
  $: localPrefixes = cardState?.localPrefixes ?? [];
  $: localProcessingEnabled = cardState?.localProcessingEnabled ?? false;
  $: pendingDeletes = cardState?.pendingDeletes ?? [];
  $: starterDataValidationResult = cardState?.starterDataValidationResult ?? null;

  $: hasPrefixes = hasValidPrefixes(localPrefixes);
  $: displayedFiles = allFiles.filter((f) => !pendingDeletes.some((pd: { filename: string; folder: DirectFeedFolder }) => pd.filename === f.name && pd.folder === f.folder));

  $: if (files) {
    allFiles = [...files.unprocessedFiles.map((f: FeedFilePending) => ({ ...f, folder: DirectFeedFolder.Ingestions, status: 'pending' }))];

    hasErrors = allFiles.some((f) => getFileErrorReasons(f).length > 0);
  }

  onMount(() => {
    initCreditCardState(creditCardType);
    loadData();
  });

  function hasCorrectFormat(file: FeedFilePending): boolean {
    if (file.canBeDecrypted === false) return false;
    if (file.accountsFound.length > 0) {
      return file.accountsFound.every((a) => a.isMasked === true);
    }
    return true;
  }

  function findFirstFileForValidation(unprocessedFiles: FeedFilePending[]): FeedFilePending | null {
    const patternGroups = [
      [/_1\./, /_1_/, /001_/],
      [/_2\./, /_2_/, /002_/],
      [/_3\./, /_3_/, /003_/],
    ];

    for (const patterns of patternGroups) {
      const candidates = unprocessedFiles.filter((f) => patterns.some((p) => p.test(f.name)) && !f.isTestFile && hasCorrectFormat(f));
      if (candidates.length > 0) return candidates[0];
    }
    return null;
  }

  async function validateVisaStarterData(processingEnabled: boolean) {
    if (creditCardType !== CreditCardType.Visa) return;
    if (processingEnabled) return;

    const hasFailedFile = files?.unprocessedFiles?.some((f) => !hasCorrectFormat(f));
    if (hasFailedFile) return;

    const targetFile = findFirstFileForValidation(files?.unprocessedFiles || []);
    if (!targetFile) return;

    try {
      await directFeedStore.validateVisaFirstFile({
        clientId,
        creditCardType,
        filename: targetFile.name,
      });
    } catch (error) {
      console.error('Starter data validation failed:', error);
    }
  }

  async function loadData() {
    loading = true;
    setStarterDataValidationResult(creditCardType, null);
    try {
      await Promise.all([loadConfig(), loadFiles()]);
      await validateVisaStarterData(config?.isProcessingEnabled ?? false);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      loading = false;
    }
  }

  async function loadConfig() {
    const configs = await directFeedStore.getConfigs(clientId);
    config = configs.find((c) => c.creditCardType === creditCardType) || null;

    if (config) {
      if (!config.filenameWhitelist) {
        config.filenameWhitelist = [];
      }

      config.filenameWhitelist = filterValidPrefixes(config.filenameWhitelist);
      initFromServerConfig(creditCardType, config.filenameWhitelist, config.isProcessingEnabled);
    } else {
      initFromServerConfig(creditCardType, [], false);
    }
  }

  async function loadFiles() {
    files = await directFeedStore.getFiles(clientId, creditCardType);
  }

  function handleToggleProcessing() {
    if (!localProcessingEnabled && !hasPrefixes) {
      return;
    }
    setLocalProcessingEnabled(creditCardType, !localProcessingEnabled);
  }

  function handlePrefixes() {
    showPrefixesModal = true;
  }

  function handlePrefixesClose(event: CustomEvent) {
    const updatedPrefixes = event.detail;
    const prefixList = Array.isArray(updatedPrefixes) ? updatedPrefixes : [];
    setLocalPrefixes(creditCardType, prefixList);
  }

  function handleFileDelete(event: CustomEvent<{ folder: DirectFeedFolder; filename: string }>) {
    const { folder, filename } = event.detail;
    addPendingDelete(creditCardType, folder, filename);
  }

  export async function saveCCRSettings() {
    if (!config || isSaving) return;

    if (localProcessingEnabled && !hasPrefixes) {
      throw [
        {
          key: 'Validation Error',
          value: 'Cannot enable sync without prefixes.',
        },
      ];
    }

    isSaving = true;

    try {
      await directFeedStore.toggleProcessing({
        clientId,
        creditCardType,
        isProcessingEnabled: localProcessingEnabled,
        prefixWhitelist: localPrefixes.map((p: FilenameWhitelist) => p.filenamePrefix),
      });

      for (const { folder, filename } of pendingDeletes) {
        await directFeedStore.deleteFile(clientId, creditCardType, folder, filename);
      }

      syncToServer(creditCardType);
      await loadFiles();
    } catch (error) {
      rollbackFromServer(creditCardType);
      await loadFiles();

      showToast({
        title: 'Error',
        message: `Failed to save ${title} settings`,
        type: 'error',
      });
      throw error;
    } finally {
      isSaving = false;
    }
  }
</script>

<div class="rounded-3xl border-2 border-surface-600 bg-white bg-opacity-90 p-4">
  <div class="mx-auto flex flex-col p-4">
    <div class="flex items-center justify-between" class:mb-4={config}>
      <div class="flex items-center gap-3">
        <h4 class="text-lg font-semibold">{title} Ingestions folder</h4>
        {#if !config}
          <span class="text-sm text-gray-500">No connection established</span>
        {:else if hasErrors}
          <span class="rounded-lg bg-red-100 px-5 py-2 text-xs text-red-700"> Processing failure </span>
        {/if}
        {#if starterDataValidationResult && !starterDataValidationResult.isValid}
          <span class="rounded-lg bg-red-100 px-5 py-2 text-xs text-red-700"> The first file is missing starter data </span>
        {/if}
        {#if config && !hasPrefixes}
          <div class="flex items-center rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2">
            <span class="text-xs text-yellow-800">At least one prefix is required to activate sync</span>
          </div>
        {/if}
      </div>

      {#if config}
        <div class="flex items-center gap-4">
          <button
            class="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
            on:click={handlePrefixes}
            disabled={loading}
          >
            <Pencil size={16} />
            Prefixes
          </button>

          <SlideToggle
            name={`${title.toLowerCase()}-sync`}
            checked={localProcessingEnabled}
            on:click={handleToggleProcessing}
            disabled={loading}
            size="sm"
            active="bg-success-500"
            background="bg-secondary-600"
          />
        </div>
      {/if}
    </div>

    {#if config}
      {#if loading}
        <div class="space-y-3">
          <div class="h-32 animate-pulse rounded-lg bg-gray-100" />
          <div class="h-32 animate-pulse rounded-lg bg-gray-100" />
          <div class="h-32 animate-pulse rounded-lg bg-gray-100" />
        </div>
      {:else if files && !files.containerExists}
        <div class="rounded-lg bg-gray-50 py-8 text-center text-gray-500">
          <p>The client doesn't have the feed set up</p>
        </div>
      {:else if files}
        <FileTable
          files={displayedFiles}
          {creditCardType}
          starterDataValidation={starterDataValidationResult
            ? {
                filename: starterDataValidationResult.filename,
                isValid: starterDataValidationResult.isValid,
                message: starterDataValidationResult.message,
                missingDataWarnings: starterDataValidationResult.missingDataWarnings,
              }
            : null}
          on:delete={handleFileDelete}
        />
      {/if}
    {/if}
  </div>
</div>

{#if showPrefixesModal}
  <PrefixesModal
    bind:isOpen={showPrefixesModal}
    prefixes={localPrefixes}
    cardType={title}
    prefixSuggestion={files?.filenamePrefixSuggestion ?? ''}
    on:close={handlePrefixesClose}
  />
{/if}
